// IssueFormFields.jsx - Updated with proper company_id handling and epic generation fix
import React, { useState, useEffect } from 'react';
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Button,
  Alert,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Snackbar
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { issueTypes, priorities as priorityList } from './issuetype.jsx';
import EpicSelectionDialog from './EpicSelection.jsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const IssueFormFields = ({
  issueData,
  errors,
  setErrors,
  projects,
  epics,
  stories,
  tasks,
  isLoading,
  isEditMode,
  isEpicsLoading,
  isStoriesLoading,
  isTasksLoading,
  handleChange,
  handleProjectChange,
  handleEpicChange,
  handleStoryChange,
  handleTaskChange,
  company_id: propCompanyId, // Renamed for clarity
  onRefreshEpics, // NEW: Callback to refresh epics after creation
  selectedProjectName, // NEW: For display purposes
  teamMembers = []
}) => {
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // 🔧 FIX: Extract company_id from localStorage/token if prop is undefined
  const [companyId, setCompanyId] = useState(propCompanyId);
  useEffect(() => {
    if (!companyId) {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const extractedCompanyId = payload.company_id;
          if (extractedCompanyId && !isNaN(Number.parseInt(extractedCompanyId))) {
            setCompanyId(extractedCompanyId);
          } else {
            //console.error('Invalid company_id in token:', extractedCompanyId);
          }
        } else {
          //console.error('No auth token found in localStorage');
        }
      } catch (error) {
        //console.error('Error extracting company_id from token:', error);
      }
    }
  }, [companyId, propCompanyId]);

  // AI Suggestions State
  const [aiSuggestions, setAiSuggestions] = useState({
    description: '',
    priority: null,
    startDate: null,
    endDate: null,
    labels: [],
    enhancedSummary: ''
  });
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Selection State for Checkboxes
  const [selectedSuggestions, setSelectedSuggestions] = useState({
    description: false,
    priority: false,
    startDate: false,
    endDate: false,
    labels: false
  });
  
  // Confirmation Dialog State
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    selectedFields: []
  });

  // Epic Generation State - UPDATED with validation
  const [epicDialogOpen, setEpicDialogOpen] = useState(false);
  const [hasPRD, setHasPRD] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [epicCreationSuccess, setEpicCreationSuccess] = useState(false);
  const [epicsExist, setEpicsExist] = useState(false); // NEW: Track if epics already exist

  // Check if selected project has PRD & epics exist - UPDATED
  useEffect(() => {
    const checkProjectPRDAndEpics = async () => {
      if (issueData.project_id && projects.length > 0) {
        const project = projects.find(p => String(p.value) === String(issueData.project_id));
        setCurrentProject(project);
        // Check if project has uploadDocument (PRD)
        const hasDocument = 
          project?.originalData?.uploadDocument || 
          project?.originalData?.document || 
          project?.originalData?.prd || 
          project?.uploadDocument || 
          project?.document || 
          project?.prd;
        

        
        setHasPRD(!!hasDocument);

        // NEW: Check if epics already exist for this project
        if (epics && epics.length > 0) {
          setEpicsExist(true);
        } else {
          setEpicsExist(false);
        }
      } else {
        setHasPRD(false);
        setCurrentProject(null);
        setEpicsExist(false);
      }
    };
    
    checkProjectPRDAndEpics();
  }, [issueData.project_id, projects, epics]);

  // Fetch AI-generated suggestions
  const generateAISuggestions = async () => {
    if (!issueData.summary || issueData.summary.trim().length < 5) {
      setAiError('Please enter a summary first (at least 5 characters)');
      return;
    }

    setIsLoadingAI(true);
    setAiError(null);
    setShowSuggestions(false);
    setSelectedSuggestions({
      description: false,
      priority: false,
      startDate: false,
      endDate: false,
      labels: false
    });

    try {
      const token = localStorage.getItem('token');
      const queryParams = new URLSearchParams({
        summary: issueData.summary,
        issueType: issueData.issueType || 4,
        company_id: companyId // Use resolved companyId
      });

      if (issueData.project_id) {
        queryParams.append('projectId', issueData.project_id);
      }
      if (issueData.epic_id) {
        queryParams.append('epicId', issueData.epic_id);
      }
      if (issueData.story_id) {
        queryParams.append('storyId', issueData.story_id);
      }
      if (issueData.startDate) {
        queryParams.append('startDate', issueData.startDate);
      }

      const response = await fetch(
        `${API_URL}/api/issues/ai-suggestions?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (result.code === "AI_PLAN_UPGRADE_REQUIRED") {
          const msg = result.message || "AI features are not included in your current plan. Please upgrade to unlock AI.";
          setSnackbar({
            open: true,
            message: msg,
            severity: "warning"
          });
          setAiError(msg);
          return;
        } else if (result.code === "AI_TOKENS_EXHAUSTED") {
          const msg = `⚠️ ${result.message || "You have used all your AI tokens for this billing cycle."}`;
          setSnackbar({
            open: true,
            message: msg,
            severity: "warning"
          });
          setAiError(msg);
          return;
        } else if (result.code === "AI_FEATURE_LOCKED") {
          const msg = result.message || "AI Issue Suggestion feature is not enabled for your plan.";
          setSnackbar({
            open: true,
            message: msg,
            severity: "warning"
          });
          setAiError(msg);
          return;
        }

        throw new Error(result.message || 'Failed to generate AI suggestions');
      }
      
      if (result.success && result.data) {
        if (result.data.priority) {
        } else {
          console.warn('Priority not in suggestions, defaulting to medium');
          result.data.priority = { priority: 3, reason: 'Default medium priority' };
        }
        
        setAiSuggestions(result.data);
        setShowSuggestions(true);
        setSnackbar({
          open: true,
          message: "✨ AI suggestions generated successfully!",
          severity: "success"
        });
      } else {
        throw new Error(result.message || 'No suggestions generated');
      }
    } catch (error) {
      //console.error('Error generating AI suggestions:', error);
      setAiError(error.message || 'Failed to generate suggestions');
      setSnackbar({
        open: true,
        message: error.message || 'Failed to generate AI suggestions',
        severity: "error"
      });
    } finally {
      setIsLoadingAI(false);
    }
  };

  // Handle checkbox selection
  const handleSuggestionSelect = (field) => {
    setSelectedSuggestions(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Open confirmation dialog with selected fields
  const openConfirmDialog = (selectedFields) => {
    setConfirmDialog({
      open: true,
      selectedFields
    });
  };

  // Get selected fields summary for confirmation
  const getSelectedFieldsSummary = (selectedFields) => {
    const summary = [];
    if (selectedFields.includes('description')) summary.push('Description');
    if (selectedFields.includes('priority')) summary.push('Priority');
    if (selectedFields.includes('startDate')) summary.push('Start Date');
    if (selectedFields.includes('endDate')) summary.push('Due Date');
    if (selectedFields.includes('labels')) summary.push('Labels');
    return summary.join(', ');
  };

  // Confirm apply selected suggestions
  const confirmApply = () => {
    const fieldsToApply = [];
    if (selectedSuggestions.description && aiSuggestions.description) {
      applySuggestion('description', aiSuggestions.description);
      fieldsToApply.push('description');
    }
    if (selectedSuggestions.priority && aiSuggestions.priority) {
      applySuggestion('priority', aiSuggestions.priority.priority);
      fieldsToApply.push('priority');
    }
    if (selectedSuggestions.startDate && aiSuggestions.startDate) {
      applySuggestion('startDate', aiSuggestions.startDate);
      fieldsToApply.push('startDate');
    }
    if (selectedSuggestions.endDate && aiSuggestions.endDate) {
      applySuggestion('endDate', aiSuggestions.endDate);
      fieldsToApply.push('endDate');
    }
    if (selectedSuggestions.labels && aiSuggestions.labels.length > 0) {
      applySuggestion('labels', aiSuggestions.labels);
      fieldsToApply.push('labels');
    }
    
    setConfirmDialog(prev => ({ ...prev, open: false }));
    setShowSuggestions(false);
  };

  // Apply individual AI suggestion
  const applySuggestion = (field, value) => {
    handleChange({
      target: {
        name: field,
        value: value
      }
    });
    
    if (field === 'summary' && aiSuggestions.enhancedSummary) {
      handleChange({
        target: {
          name: 'summary',
          value: aiSuggestions.enhancedSummary
        }
      });
    }
  };

  // Get priority name by ID
  const getPriorityName = (priorityId) => {
    const priority = priorityList.find(p => p.id === priorityId);
    return priority ? priority.name : 'Medium';
  };

  // Handle Epic Generation Dialog - UPDATED with companyId validation
  const handleOpenEpicDialog = () => {
    // 🔧 FIX: Validate companyId before opening dialog
    if (!companyId || isNaN(Number.parseInt(companyId))) {
      //console.error("Invalid company_id:", companyId);
      alert("Company ID is required to generate epics. Please refresh and log in again.");
      return;
    }
    setEpicDialogOpen(true);
    setEpicCreationSuccess(false);
  };

  const handleCloseEpicDialog = () => {
    setEpicDialogOpen(false);
  };

  // Handle Epic Creation Success - UPDATED
  const handleEpicsConfirmed = (createdEpics) => {
    setEpicCreationSuccess(true);
    setEpicsExist(true); // NEW: Mark epics as existing now
    
    // Refresh epics list in parent component
    if (onRefreshEpics) {
      onRefreshEpics();
    }
    
    // Auto-hide success message after 5 seconds
    setTimeout(() => {
      setEpicCreationSuccess(false);
    }, 5000);
  };



const renderHierarchicalDependency = () => {
  const issueTypeId = parseInt(issueData.issueType);
  
  if (!issueData.project_id) {
    return (
      <Grid item xs={12}>
        <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
          Please select a project first to see hierarchy options.
        </Typography>
      </Grid>
    );
  }

  // Epic - No parent required
  if (issueTypeId === 2) {
    return (
      <Grid item xs={12}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> Epics are top-level items and don't require hierarchy selection.
          </Typography>
        </Alert>
      </Grid>
    );
  }

  // Story - Requires Epic
  if (issueTypeId === 3) {
    return (
      <Grid item xs={12}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Important:</strong> Story requires an Epic. Please select an Epic from the dropdown below.
          </Typography>
        </Alert>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required error={!!errors.epic_id}>
            <InputLabel>Epic *</InputLabel>
            <Select
              name="epic_id"
              value={issueData.epic_id || ''}
              label="Epic *"
              onChange={handleEpicChange}
              disabled={isEpicsLoading || epics.length === 0}
            >
              <MenuItem value="">Select Epic</MenuItem>
              {epics.map((epic) => (
                <MenuItem key={epic.value} value={epic.value}>
                  {epic.label}
                </MenuItem>
              ))}
            </Select>
            {errors.epic_id && (
              <Typography color="error" variant="caption">{errors.epic_id}</Typography>
            )}
            {!errors.epic_id && (isEpicsLoading ? (
              <Typography variant="caption" color="text.secondary">Loading epics...</Typography>
            ) : epics.length === 0 ? (
              <Typography variant="caption" color="warning.main">No epics found. Create an epic first or select a different project.</Typography>
            ) : null)}
          </FormControl>
        </Grid>
      </Grid>
    );
  }

  // Task - Requires Story
  if (issueTypeId === 4) {
    return (
      <>
        <Grid item xs={12}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Important:</strong> Task requires a Story. Please select a Story from the dropdown below.
            </Typography>
          </Alert>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Epic (Optional)</InputLabel>
            <Select
              name="epic_id"
              value={issueData.epic_id || ''}
              label="Epic (Optional)"
              onChange={handleEpicChange}
              disabled={isEpicsLoading || epics.length === 0}
            >
              <MenuItem value="">None</MenuItem>
              {epics.map((epic) => (
                <MenuItem key={epic.value} value={epic.value}>
                  {epic.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required error={!!errors.story_id}>
            <InputLabel>Story *</InputLabel>
            <Select
              name="story_id"
              value={issueData.story_id || ''}
              label="Story *"
              onChange={handleStoryChange}
              disabled={isStoriesLoading || stories.length === 0}
            >
              <MenuItem value="">Select Story</MenuItem>
              {stories.map((story) => (
                <MenuItem key={story.value} value={story.value}>
                  {story.label}
                </MenuItem>
              ))}
            </Select>
            {errors.story_id && (
              <Typography color="error" variant="caption">{errors.story_id}</Typography>
            )}
            {!errors.story_id && (isStoriesLoading ? (
              <Typography variant="caption" color="text.secondary">Loading stories...</Typography>
            ) : stories.length === 0 ? (
              <Typography variant="caption" color="warning.main">
                {issueData.epic_id ? 'No stories found for this epic.' : 'Select an epic first to see stories.'}
              </Typography>
            ) : null)}
          </FormControl>
        </Grid>
      </>
    );
  }

  // Bug - Requires Task
  if (issueTypeId === 1) {
    return (
      <>
        <Grid item xs={12}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Important:</strong> Bug requires a Task. Please select a Task from the dropdown below.
            </Typography>
          </Alert>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Epic (Optional)</InputLabel>
            <Select
              name="epic_id"
              value={issueData.epic_id || ''}
              label="Epic (Optional)"
              onChange={handleEpicChange}
              disabled={isEpicsLoading || epics.length === 0}
            >
              <MenuItem value="">None</MenuItem>
              {epics.map((epic) => (
                <MenuItem key={epic.value} value={epic.value}>
                  {epic.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Story (Optional)</InputLabel>
            <Select
              name="story_id"
              value={issueData.story_id || ''}
              label="Story (Optional)"
              onChange={handleStoryChange}
              disabled={isStoriesLoading || stories.length === 0}
            >
              <MenuItem value="">None</MenuItem>
              {stories.map((story) => (
                <MenuItem key={story.value} value={story.value}>
                  {story.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth required error={!!errors.task_id}>
            <InputLabel>Task *</InputLabel>
            <Select
              name="task_id"
              value={issueData.task_id || ''}
              label="Task *"
              onChange={handleTaskChange}
              disabled={isTasksLoading || tasks.length === 0}
            >
              <MenuItem value="">Select Task</MenuItem>
              {tasks.map((task) => (
                <MenuItem key={task.value} value={task.value}>
                  {task.label}
                </MenuItem>
              ))}
            </Select>
            {errors.task_id && (
              <Typography color="error" variant="caption">{errors.task_id}</Typography>
            )}
            {!errors.task_id && (isTasksLoading ? (
              <Typography variant="caption" color="text.secondary">Loading tasks...</Typography>
            ) : tasks.length === 0 ? (
              <Typography variant="caption" color="warning.main">
                {issueData.story_id ? 'No tasks found for this story.' : 'Select a story first to see tasks.'}
              </Typography>
            ) : null)}
          </FormControl>
        </Grid>
      </>
    );
  }

  // Change Request (5) - Flexible
  if (issueTypeId === 5) {
    return (
      <Grid item xs={12}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> Change Request can be created at any level. Hierarchy selection is optional.
          </Typography>
        </Alert>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Epic (Optional)</InputLabel>
              <Select
                name="epic_id"
                value={issueData.epic_id || ''}
                label="Epic (Optional)"
                onChange={handleEpicChange}
                disabled={isEpicsLoading || epics.length === 0}
              >
                <MenuItem value="">None</MenuItem>
                {epics.map((epic) => (
                  <MenuItem key={epic.value} value={epic.value}>
                    {epic.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Story (Optional)</InputLabel>
              <Select
                name="story_id"
                value={issueData.story_id || ''}
                label="Story (Optional)"
                onChange={handleStoryChange}
                disabled={isStoriesLoading || stories.length === 0}
              >
                <MenuItem value="">None</MenuItem>
                {stories.map((story) => (
                  <MenuItem key={story.value} value={story.value}>
                    {story.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Task (Optional)</InputLabel>
              <Select
                name="task_id"
                value={issueData.task_id || ''}
                label="Task (Optional)"
                onChange={handleTaskChange}
                disabled={isTasksLoading || tasks.length === 0}
              >
                <MenuItem value="">None</MenuItem>
                {tasks.map((task) => (
                  <MenuItem key={task.value} value={task.value}>
                    {task.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  return null;
};

  return (
    <>
    

      {/* Project Select */}
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth required error={!!errors.project_id}>
          <InputLabel>Project</InputLabel>
          <Select
            name="project_id"
            value={issueData.project_id || ''}
            label="Project"
            onChange={handleProjectChange}
            disabled={isLoading || projects.length === 0 || (isEditMode && !issueData.project_id) || selectedProjectName} // NEW: Disable if auto-selected
          >
            {projects.length > 0 ? (
              projects.map((project) => (
                <MenuItem key={project.value} value={project.value}>
                  {project.label}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled value="">No projects available</MenuItem>
            )}
          </Select>
          {errors.project_id && (
            <Typography color="error" variant="caption">{errors.project_id}</Typography>
          )}
        </FormControl>
      </Grid>

<Grid item xs={12} sm={6}>
  <FormControl fullWidth required error={!!errors.issueType}>
    <InputLabel>Issue Type</InputLabel>
    <Select
      name="issueType"
      value={issueData.issueType || ''}
      label="Issue Type"
      onChange={(e) => {
        handleChange(e);
        // Clear hierarchy errors when issue type changes
        if (setErrors) {
          setErrors(prev => ({
            ...prev,
            epic_id: null,
            story_id: null,
            task_id: null
          }));
        }
      }}
      disabled={isLoading}
    >
      {issueTypes.map((type) => {
        let requirementText = "";
        switch(type.id) {
          case 1: // Bug
            requirementText = " (Requires Task)";
            break;
          case 2: // Epic
            requirementText = " (No hierarchy required)";
            break;
          case 3: // Story
            requirementText = " (Requires Epic)";
            break;
          case 4: // Task
            requirementText = " (Requires Story)";
            break;
          case 5: // Change Request
            requirementText = " (Optional hierarchy)";
            break;
        }
        
        return (
          <MenuItem key={type.id} value={type.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {type.icon}
                <Typography sx={{ ml: 1 }}>{type.name}</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                {requirementText}
              </Typography>
            </Box>
          </MenuItem>
        );
      })}
    </Select>
    {errors.issueType && (
      <Typography color="error" variant="caption">{errors.issueType}</Typography>
    )}
  </FormControl>
</Grid>

      {/* PRD Detection Alert & Epic Generation Button - UPDATED with epicsExist check */}
      <Grid item xs={12}>
        <Collapse in={issueData.project_id && !isEditMode}>
          <Alert 
            severity={epicsExist ? "success" : hasPRD ? "info" : "warning"}
            icon={<DescriptionIcon />}
            action={
              !epicsExist && (
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  onClick={handleOpenEpicDialog}
                  startIcon={<AutoAwesomeIcon />}
                  disabled={!companyId} // Disable if no companyId
                  sx={{ textTransform: 'none' }}
                >
                  {companyId ? 'Generate Epics' : 'Login Required'}
                </Button>
              )
            }
            sx={{ mb: 2 }}
          >
            {epicsExist ? (
              <>
                <Typography variant="body2" fontWeight="medium">
                  Epics Already Created
                </Typography>
                <Typography variant="body2">
                  {epics.length} epic(s) already exist for this project. You can select them in the hierarchy section below.
                </Typography>
              </>
            ) : hasPRD ? (
              <>
                <Typography variant="body2" fontWeight="medium">
                  PRD Document Detected
                </Typography>
                <Typography variant="body2">
                  This project has a requirements document. Click "Generate Epics" to automatically extract epics from the PRD.
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body2" fontWeight="medium">
                  Generate Epics from PRD (Test Mode)
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                  Click to test epic generation. (PRD detection: {hasPRD ? 'Found' : 'Not found'})
                </Typography>
              </>
            )}
          </Alert>
        </Collapse>
      </Grid>

      {/* Epic Creation Success Message */}
      <Collapse in={epicCreationSuccess}>
        <Grid item xs={12}>
          <Alert 
            severity="success" 
            icon={<CheckCircleOutlineIcon />}
            onClose={() => setEpicCreationSuccess(false)}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2">
              Epics created successfully! The epic dropdown is now populated with AI-generated epics.
            </Typography>
          </Alert>
        </Grid>
      </Collapse>

    

      {/* Summary */}
      <Grid item xs={12}>
        <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" fontWeight="medium">
            Summary *
          </Typography>
        </Box>
        <TextField
          required
          fullWidth
          name="summary"
          label="Summary"
          value={issueData.summary || ''}
          onChange={handleChange}
          error={!!errors.summary}
          helperText={errors.summary || "Enter a clear, concise summary of the issue"}
          disabled={isLoading}
        />
      </Grid>

      {/* AI Suggestions Panel - With Checkboxes */}
      {showSuggestions && (
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
            <Typography variant="h6" color="primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoAwesomeIcon />
              AI Suggestions - Select to Apply
            </Typography>
            
            <List>
              {/* Description */}
              {aiSuggestions.description && (
                <ListItem>
                  <ListItemIcon>
                    <Checkbox
                      checked={selectedSuggestions.description}
                      onChange={() => handleSuggestionSelect('description')}
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Description" 
                    secondary={
                      <React.Fragment>
                        <Typography component="span" variant="body2" color="text.primary">
                          Apply AI-generated description?
                        </Typography>
                        <br />
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          Preview: {aiSuggestions.description.substring(0, 100)}...
                        </Typography>
                      </React.Fragment>
                    }
                  />
                </ListItem>
              )}

              {/* Priority */}
              {aiSuggestions.priority && (
                <ListItem>
                  <ListItemIcon>
                    <Checkbox
                      checked={selectedSuggestions.priority}
                      onChange={() => handleSuggestionSelect('priority')}
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Priority" 
                    secondary={
                      <React.Fragment>
                        <Typography component="span" variant="body2" color="text.primary">
                          Set to {getPriorityName(aiSuggestions.priority.priority)}
                        </Typography>
                        <br />
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          Reason: {aiSuggestions.priority.reason}
                        </Typography>
                      </React.Fragment>
                    }
                  />
                </ListItem>
              )}

              {/* Start Date */}
              {aiSuggestions.startDate && (
                <ListItem>
                  <ListItemIcon>
                    <Checkbox
                      checked={selectedSuggestions.startDate}
                      onChange={() => handleSuggestionSelect('startDate')}
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Start Date" 
                    secondary={`Set to ${aiSuggestions.startDate}`}
                  />
                </ListItem>
              )}

              {/* End Date */}
              {aiSuggestions.endDate && (
                <ListItem>
                  <ListItemIcon>
                    <Checkbox
                      checked={selectedSuggestions.endDate}
                      onChange={() => handleSuggestionSelect('endDate')}
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Due Date" 
                    secondary={`Set to ${aiSuggestions.endDate}`}
                  />
                </ListItem>
              )}

              {/* Labels */}
              {aiSuggestions.labels && aiSuggestions.labels.length > 0 && (
                <ListItem>
                  <ListItemIcon>
                    <Checkbox
                      checked={selectedSuggestions.labels}
                      onChange={() => handleSuggestionSelect('labels')}
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Labels" 
                    secondary={`Apply: ${aiSuggestions.labels.join(', ')}`}
                  />
                </ListItem>
              )}
            </List>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => setShowSuggestions(false)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  const selectedFields = Object.keys(selectedSuggestions).filter(key => selectedSuggestions[key]);
                  if (selectedFields.length === 0) {
                    alert('Please select at least one suggestion to apply.');
                    return;
                  }
                  openConfirmDialog(selectedFields);
                }}
                disabled={Object.values(selectedSuggestions).every(val => !val)}
              >
                Confirm & Apply Selected
              </Button>
            </Box>
          </Paper>
        </Grid>
      )}

      {/* Description with AI Button */}
      <Grid item xs={12}>
        <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" fontWeight="medium">
            Description *
          </Typography>
          <Button
            size="small"
            variant="contained"
            startIcon={isLoadingAI ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
            onClick={generateAISuggestions}
            disabled={isLoadingAI || !issueData.summary || issueData.summary.trim().length < 5}
            sx={{ 
              textTransform: 'none',
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              boxShadow: '0 3px 5px 2px rgba(102, 126, 234, .3)'
            }}
          >
            {isLoadingAI ? 'Generating...' : 'Generate AI Suggestions'}
          </Button>
        </Box>
        
        <TextField
          required
          fullWidth
          name="description"
          multiline
          rows={12}
          value={issueData.description || ''}
          onChange={handleChange}
          placeholder="Describe the issue in detail... Click 'Generate AI Suggestions' to auto-generate"
          disabled={isLoading}
          error={!!errors.description}
          helperText={errors.description}
        />
        
        {aiError && (
          <Alert severity="error" onClose={() => setAiError(null)} sx={{ mt: 1 }}>
            {aiError}
          </Alert>
        )}
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
      >
        <DialogTitle>Confirm Apply Suggestions</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Apply the following selected suggestions?
            <br />
            <strong>{getSelectedFieldsSummary(confirmDialog.selectedFields)}</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))}>
            Cancel
          </Button>
          <Button onClick={confirmApply} color="primary" variant="contained">
            Confirm Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Epic Selection Dialog - UPDATED with resolved companyId */}
      <EpicSelectionDialog
        open={epicDialogOpen}
        onClose={handleCloseEpicDialog}
        projectId={issueData.project_id}
        projectName={currentProject?.label || ''}
        companyId={companyId} // Pass resolved companyId
        onEpicsConfirmed={handleEpicsConfirmed}
        teamMembers={teamMembers}
        leadId={currentProject?.originalData?.lead_id || null}
        productOwnerId={currentProject?.originalData?.lead_id || currentProject?.originalData?.manager_id || null}
      />

      {/* Top Center Alert for AI Feedback & Limits */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%', boxShadow: 3 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default IssueFormFields;